import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { ChevronDown, ChevronUp, Plus, Minus, Calendar, X } from "lucide-react";
import { format, isSameDay } from "date-fns";
import { ru } from "date-fns/locale";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { getDateFString } from "../../lib/utils";
import { Button } from "../ui/button";
import ServerAPI from "../../lib/server";
import PrintTariffPaymentConditions from "./PrintTariffPaymentConditions";
import searchStore from "../../store/searchStore";
import { useCapacityLimits } from "../../hooks/useCapacityLimits";

const MONTHS_RU = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];
const DAYS_RU = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const CURRENT_MONTH_IDX = 6;
const MAX_DAYS = 42;

// Предвычисляем константы
const getMonthList = (() => {
  let cached = null;
  return () => {
    if (cached) return cached;
    const today = new Date();
    const list = [];
    for (let i = -6; i <= 6; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      list.push({ year: d.getFullYear(), month: d.getMonth() });
    }
    cached = list;
    return list;
  };
})();

// Кэшируем календарные дни для каждого месяца
const calendarCache = new Map();
function getCalendarDays(year, month) {
  const key = `${year}-${month}`;
  if (calendarCache.has(key)) return calendarCache.get(key);

  const first = new Date(year, month, 1);
  let startDow = first.getDay();
  startDow = startDow === 0 ? 6 : startDow - 1;

  const days = [];
  const firstDate = new Date(year, month, 1);

  for (let i = 0; i < startDow; i++) {
    days.push({
      date: new Date(year, month, 1 - (startDow - i)),
      currentMonth: false,
    });
  }

  const lastDay = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= lastDay; d++) {
    days.push({ date: new Date(year, month, d), currentMonth: true });
  }

  const rem = MAX_DAYS - days.length;
  for (let i = 1; i <= rem; i++) {
    days.push({ date: new Date(year, month + 1, i), currentMonth: false });
  }

  calendarCache.set(key, days);
  return days;
}

// Мемоизированный компонент дня для предотвращения лишних перерисовок
const DayCell = React.memo(
  ({
    date,
    currentMonth,
    isStart,
    isEnd,
    isInRange,
    isFirstUnavailable,
    isWeekend,
    available,
    blocked,
    price,
    accentColor,
    textColor,
    onDayClick,
    onHover,
    onLeave,
    canSelect,
  }) => {
    const dayClasses = useMemo(() => {
      let base =
        "relative flex flex-col items-center text-[14px] justify-start pt-0.5 h-10 select-none transition-colors z-10";

      // Вычисляем, является ли дата прошедшей
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateOnly = new Date(date);
      dateOnly.setHours(0, 0, 0, 0);
      const isPastDate = dateOnly < today;

      if (!isStart && !isEnd && !blocked && currentMonth && !isPastDate) {
        base += " cursor-pointer hover:bg-teal-50 rounded";
      }

      if (blocked || isPastDate || (!currentMonth && !isStart && !isEnd)) {
        base += " cursor-default";
      }

      if (isStart || isEnd) {
        base += " font-semibold";
      } else if (isInRange) {
        base += " text-teal-800";
      } else if (isPastDate && !isStart && !isEnd) {
        base += " text-gray-300 opacity-50";
      } else if (!currentMonth) {
        base += " text-gray-200";
      } else if (isWeekend && !blocked) {
        base += " text-red-500 font-semibold";
      } else if (!blocked) {
        base += " text-gray-800";
      } else {
        base += " text-gray-300";
      }

      return base;
    }, [isStart, isEnd, blocked, currentMonth, isInRange, isWeekend, date]);

    // Вычисляем isPastDate еще раз для использования в рендеринге
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    const isPastDate = dateOnly < today;

    return (
      <div
        className="relative"
        onMouseEnter={canSelect ? () => onHover(date) : undefined}
        onMouseLeave={canSelect ? onLeave : undefined}
      >
        {isInRange && (
          <div
            className="absolute"
            style={{
              inset: "0 0 8px 0",
              backgroundColor: `${accentColor}18`,
            }}
          />
        )}
        {isStart && (
          <>
            <div
              className="absolute"
              style={{
                top: 0,
                bottom: 8,
                left: "50%",
                right: 0,
                backgroundColor: `${accentColor}18`,
              }}
            />
            <div
              className="absolute rounded-full"
              style={{
                top: 0,
                bottom: 8,
                left: -3,
                right: -3,
                zIndex: 2,
                backgroundColor: accentColor,
                color: textColor,
              }}
            />
          </>
        )}
        {isEnd && (
          <>
            <div
              className="absolute"
              style={{
                top: 0,
                bottom: 8,
                left: 0,
                right: "50%",
                backgroundColor: `${accentColor}18`,
              }}
            />
            <div
              className="absolute rounded-full"
              style={{
                top: 0,
                bottom: 8,
                left: -3,
                right: -3,
                zIndex: 2,
                backgroundColor: accentColor,
                color: textColor,
              }}
            />
          </>
        )}
        {isFirstUnavailable && !isStart && !isEnd && (
          <div
            className="absolute rounded"
            style={{
              top: 0,
              bottom: 8,
              left: 0,
              right: 0,
              backgroundColor: `${accentColor}18`,
              border: `1px dashed ${accentColor}40`,
            }}
          />
        )}
        <div onClick={() => onDayClick(date)} className={dayClasses}>
          <span className="relative z-10 leading-none mt-0.5">
            {date.getDate()}
          </span>
          {available &&
            !blocked &&
            currentMonth &&
            !isPastDate &&
            typeof price === "number" && (
              <span
                className="relative z-10 leading-none mt-0.5"
                style={{
                  fontSize: "9px",
                  color: isStart || isEnd ? "white" : "#22c55e",
                }}
              >
                {price.toLocaleString("ru-RU")} ₽
              </span>
            )}
          {currentMonth &&
            !isStart &&
            !isEnd &&
            (blocked || isPastDate) &&
            !isFirstUnavailable && (
              <svg
                className="absolute z-20 pointer-events-none"
                style={{
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  opacity: 0.6,
                }}
                width="16"
                height="16"
                viewBox="0 0 16 16"
              >
                <line
                  x1="3"
                  y1="3"
                  x2="13"
                  y2="13"
                  stroke="#f87171"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
                <line
                  x1="13"
                  y1="3"
                  x2="3"
                  y2="13"
                  stroke="#f87171"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
              </svg>
            )}
        </div>
      </div>
    );
  },
);

// окно просмотра свободных дат
export default function RoomBookingDialog({
  open,
  onOpenChange,
  room,
  onBooking,
  hotelData,
  accentColor = "#14b8a6",
  textColor,
  onInfo = null,
}) {
  const [tariffs, setTariffs] = useState({
    prices: [],
    variants: [],
    count: 0,
  });
  const [calendarDates, setCalendarDates] = useState({});
  const [adults, setAdults] = useState(searchStore.guestsCount);
  const [childrenAges, setChildrenAges] = useState(searchStore.guestsChildren);
  const [showAgeDropdown, setShowAgeDropdown] = useState(false);
  const [withPet, setWithPet] = useState(false);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [activeMonthIdx, setActiveMonthIdx] = useState(CURRENT_MONTH_IDX);
  const [hovered, setHovered] = useState(null);
  const [showTariffs, setShowTariffs] = useState(false);

  const calendarScrollRef = useRef(null);
  const sidebarRef = useRef(null);
  const monthRefs = useRef([]);
  const sidebarItemRefs = useRef([]);
  const ageDropdownRef = useRef(null);
  const monthList = useMemo(() => getMonthList(), []);

  // Получаем capacity из стора
  const capacity = searchStore.selectedHotel?.capacity;
  // Динамические лимиты на основе выбранных гостей
  const limits = useCapacityLimits(
    room?.capacity || capacity,
    adults,
    childrenAges.length,
  );

  useEffect(() => {
    searchStore.setGuestsCount(adults);
  }, [adults]);

  useEffect(() => {
    searchStore.setGuestChildren(childrenAges);
  }, [childrenAges]);

  useEffect(() => {
    searchStore.setDogFriendly(withPet);
  }, [withPet]);

  // Предвычисляем диапазон дат, чтобы избежать повторных вычислений
  const dateRangeInfo = useMemo(() => {
    if (!checkIn) return { nights: 0, periodPrice: 0, dateLabel: null };

    const effectiveEnd = checkOut || hovered;
    const nights = checkOut
      ? Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24))
      : 0;

    let periodPrice = 0;
    if (checkOut) {
      let current = new Date(checkIn);
      while (current < checkOut) {
        const dateKey = format(current, "yyyy-MM-dd");
        const price = calendarDates[dateKey];
        if (typeof price === "number") periodPrice += price;
        current.setDate(current.getDate() + 1);
      }
    }

    let dateLabel = null;
    if (checkIn && checkOut) {
      dateLabel = `${format(checkIn, "d MMM", { locale: ru })} — ${format(checkOut, "d MMM", { locale: ru })}`;
    } else if (checkIn) {
      dateLabel = format(checkIn, "d MMM", { locale: ru });
    }

    return { nights, periodPrice, effectiveEnd, dateLabel };
  }, [checkIn, checkOut, hovered, calendarDates]);

  // Мемоизированные функции для работы с датами
  const isDateAvailable = useCallback(
    (date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dateOnly = new Date(date);
      dateOnly.setHours(0, 0, 0, 0);

      // Блокируем прошлые даты
      if (dateOnly < today) return false;

      const dateKey = format(date, "yyyy-MM-dd");
      const value = calendarDates[dateKey];
      return value !== undefined && value !== null && typeof value === "number";
    },
    [calendarDates],
  );

  const isDateBlocked = useCallback(
    (date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dateOnly = new Date(date);
      dateOnly.setHours(0, 0, 0, 0);

      // Все прошлые даты считаем заблокированными
      if (dateOnly < today) return true;

      const dateKey = format(date, "yyyy-MM-dd");
      return (
        calendarDates.hasOwnProperty(dateKey) && calendarDates[dateKey] === null
      );
    },
    [calendarDates],
  );

  const getDatePrice = useCallback(
    (date) => {
      const dateKey = format(date, "yyyy-MM-dd");
      return calendarDates[dateKey] || null;
    },
    [calendarDates],
  );

  const isWeekend = useCallback((date) => {
    const dow = date.getDay();
    return dow === 0 || dow === 6;
  }, []);

  // Close age dropdown on outside click
  useEffect(() => {
    if (!showAgeDropdown) return;
    const handler = (e) => {
      if (
        ageDropdownRef.current &&
        !ageDropdownRef.current.contains(e.target)
      ) {
        setShowAgeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showAgeDropdown]);

  // подгрузка тарифов с дебаунсом
  useEffect(() => {
    const ci = getDateFString(checkIn);
    const co = getDateFString(checkOut);
    if (!ci || !co || ci >= co) {
      return;
    }

    const timer = setTimeout(() => {
      ServerAPI.customPostQuery(`/booking/price/${hotelData._id}/${room.id}`, {
        start_date: ci,
        end_date: co,
        guests: adults,
        children: childrenAges,
        dog_friendly: withPet,
      })
        .then((res) => {
          setTariffs(res.tariff);
        })
        .catch((err) => {
          console.log("err!", err);
          searchStore.openInfoPopup(
            {
              text: <>{err || "Ошибка при подсчете цен"}</>,
            },
            () => null,
          );
          clearDates();
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [checkIn, checkOut, adults, childrenAges, withPet, room]);

  // Загрузка календаря
  useEffect(() => {
    if (!open || !hotelData) {
      return;
    }

    let cancelled = false;
    ServerAPI.customPostQuery(`/booking/calendar/${hotelData._id}/${room.id}`, {
      guests: adults,
      children: childrenAges,
      dog_friendly: withPet,
    }).then((res) => {
      if (!cancelled) setCalendarDates(res.data);
    });

    return () => {
      cancelled = true;
    };
  }, [open, adults, childrenAges, withPet, room, hotelData]);

  // Scroll to current month when calendar opens
  useEffect(() => {
    if (!open) {
      clearDates();
      return;
    }
    const timer = setTimeout(() => {
      const el = monthRefs.current[CURRENT_MONTH_IDX];
      if (el && calendarScrollRef.current) {
        calendarScrollRef.current.scrollTop = el.offsetTop;
        setActiveMonthIdx(CURRENT_MONTH_IDX);
      }
    }, 20);
    return () => clearTimeout(timer);
  }, [open]);

  // Scroll handler для календаря
  useEffect(() => {
    if (!open) return;
    const container = calendarScrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      let active = 0;
      monthRefs.current.forEach((el, i) => {
        if (el && el.offsetTop <= scrollTop + 8) active = i;
      });
      setActiveMonthIdx(active);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [open]);

  // Scroll sidebar
  useEffect(() => {
    const el = sidebarItemRefs.current[activeMonthIdx];
    const container = sidebarRef.current;
    if (el && container) {
      const elTop = el.offsetTop;
      const elHeight = el.offsetHeight;
      const containerHeight = container.clientHeight;
      container.scrollTo({
        top: elTop - containerHeight / 2 + elHeight / 2,
        behavior: "smooth",
      });
    }
  }, [activeMonthIdx]);

  const handleDayClick = useCallback(
    (date) => {
      if (!checkIn) {
        // Если дата занята или недоступна - не даём выбрать как заезд
        if (isDateBlocked(date) || !isDateAvailable(date)) {
          return;
        }
        setCheckIn(date);
        setCheckOut(null);
        return;
      }

      if (checkIn && checkOut) {
        // Начинаем новый выбор, но только если дата доступна
        if (isDateBlocked(date) || !isDateAvailable(date)) {
          return;
        }
        setCheckIn(date);
        setCheckOut(null);
        return;
      }

      if (date <= checkIn) {
        // Если дата раньше или равна заезду - разрешаем только если доступна
        if (isDateBlocked(date) || !isDateAvailable(date)) {
          return;
        }
        setCheckIn(date);
        setCheckOut(null);
        return;
      }

      // Проверяем, есть ли недоступные даты в диапазоне
      let current = new Date(checkIn);
      current.setDate(current.getDate() + 1);
      let blockedCount = 0;
      let firstBlockedDate = null;

      while (current < date) {
        if (!isDateAvailable(current) || isDateBlocked(current)) {
          blockedCount++;
          if (!firstBlockedDate) {
            firstBlockedDate = new Date(current);
          }
        }
        current.setDate(current.getDate() + 1);
      }

      if (blockedCount === 0) {
        // Все даты свободны - устанавливаем выезд
        setCheckOut(date);
      } else if (
        blockedCount === 1 &&
        firstBlockedDate &&
        isSameDay(firstBlockedDate, date)
      ) {
        // Единственная занятая дата - это та, на которую кликнули (первая занятая после свободных)
        setCheckOut(date);
      } else {
        // Больше одной занятой даты или занятая дата не последняя в диапазоне
        // Начинаем новый выбор с этой даты, но только если она доступна
        if (isDateBlocked(date) || !isDateAvailable(date)) {
          return;
        }
        setCheckIn(date);
        setCheckOut(null);
      }
    },
    [checkIn, checkOut, isDateAvailable, isDateBlocked],
  );

  const clearDates = () => {
    setCheckIn(null);
    setCheckOut(null);
  };

  const handleHover = useCallback(
    (date) => {
      if (checkIn && !checkOut && date > checkIn) {
        let current = new Date(checkIn);
        current.setDate(current.getDate() + 1);
        let blockedCount = 0;
        let firstBlockedDate = null;

        while (current < date) {
          if (!isDateAvailable(current) || isDateBlocked(current)) {
            blockedCount++;
            if (!firstBlockedDate) {
              firstBlockedDate = new Date(current);
            }
          }
          current.setDate(current.getDate() + 1);
        }

        // Разрешаем ховер если нет занятых дат или единственная занятая - это текущая дата
        if (
          blockedCount === 0 ||
          (blockedCount === 1 &&
            firstBlockedDate &&
            isSameDay(firstBlockedDate, date))
        ) {
          setHovered(date);
        }
      }
    },
    [checkIn, checkOut, isDateAvailable, isDateBlocked],
  );

  const pluralVariants = useCallback((n) => {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 19) return "вариантов";
    if (mod10 === 1) return "вариант";
    if (mod10 >= 2 && mod10 <= 4) return "варианта";
    return "вариантов";
  }, []);

  const bookingHandler = useCallback(
    (e) => {
      onOpenChange(false);
      onBooking(
        room.hotel,
        room.id,
        e.target.dataset.tariff,
        e.target.dataset.food,
        room?.selected_number || null,
        getDateFString(checkIn),
        getDateFString(checkOut),
      );
    },
    [onOpenChange, onBooking, room, checkIn, checkOut],
  );

  const goToMonthIdx = useCallback((idx) => {
    setActiveMonthIdx(idx);
    const el = monthRefs.current[idx];
    if (el && calendarScrollRef.current) {
      calendarScrollRef.current.scrollTo({
        top: el.offsetTop,
        behavior: "smooth",
      });
    }
  }, []);

  const { effectiveEnd, dateLabel } = dateRangeInfo;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-full p-0 overflow-hidden bg-white booking-dialog-content">
        <DialogHeader className="px-5 pt-3 pb-0">
          <DialogTitle className="text-lg font-bold">
            {room?.name || "Бронирование номера"}
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 pt-2 space-y-2">
          {/* Adults */}
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900 text-sm">
                Взрослые
              </div>
              <div className="text-xs text-gray-500">старше 18 лет</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAdults(Math.max(1, adults - 1))}
                className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
              >
                <Minus className="w-3 h-3" />
              </button>
              <span className="text-sm font-semibold w-6 text-center">
                {adults}
              </span>
              <button
                onClick={() => setAdults(adults + 1)}
                disabled={!limits.canAddAdult}
                className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Children */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative" ref={ageDropdownRef}>
              <button
                onClick={() => setShowAgeDropdown((v) => !v)}
                className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                disabled={!limits.canAddChild}
              >
                <Plus className="w-3.5 h-3.5" />
                Ребёнок
              </button>
              {showAgeDropdown && (
                <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-36 max-h-52 overflow-y-auto">
                  {Array.from({ length: 17 }, (_, i) => i + 1).map((age) => (
                    <button
                      key={age}
                      onClick={() => {
                        setChildrenAges((prev) => [...prev, age]);
                        setShowAgeDropdown(false);
                      }}
                      className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      {age} {age === 1 ? "год" : age < 5 ? "года" : "лет"}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {childrenAges.map((age, idx) => (
              <span
                key={idx}
                className="flex items-center gap-1 bg-gray-100 rounded-full px-2.5 py-1 text-sm text-gray-700"
              >
                {age} {age === 1 ? "год" : age < 5 ? "года" : "лет"}
                <button
                  onClick={() =>
                    setChildrenAges((prev) => prev.filter((_, i) => i !== idx))
                  }
                  className="text-gray-400 hover:text-gray-600 leading-none ml-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>

          {/* Pet + Date row */}
          <div className="flex items-center justify-between pb-0">
            <button
              onClick={() => setWithPet(!withPet)}
              className={`text-sm transition-colors ${withPet ? "text-gray-900 font-medium" : "text-gray-500"}`}
            >
              {withPet ? "Едем с собакой" : "Едем без собаки"}
            </button>

            <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-1.5">
              <Calendar className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
              <span className="text-xs text-gray-700 font-medium whitespace-nowrap">
                {dateLabel || "Заезд — Выезд"}
              </span>
              {(checkIn || checkOut) && (
                <button
                  onClick={() => clearDates()}
                  className="ml-1 text-gray-400 hover:text-gray-600 leading-none"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Calendar + Book area */}
        <div className="relative overflow-hidden">
          <div
            className="mt-1 border-t border-gray-100 flex"
            style={{ height: 290 }}
          >
            {/* Month sidebar */}
            <div
              ref={sidebarRef}
              className="w-14 flex-shrink-0 border-r border-gray-100 py-2"
              style={{
                overflowY: "scroll",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <style>{`.rmbd-sidebar::-webkit-scrollbar{display:none}.rmbd-cal::-webkit-scrollbar{display:none}`}</style>
              {monthList.map(({ year, month }, idx) => {
                const isActive = idx === activeMonthIdx;
                const showYear = idx > 0 && year !== monthList[idx - 1].year;
                return (
                  <div
                    key={`${year}-${month}`}
                    ref={(el) => (sidebarItemRefs.current[idx] = el)}
                  >
                    {showYear && (
                      <div className="text-center text-[9px] text-gray-400 font-medium py-0.5">
                        {year}
                      </div>
                    )}
                    <button
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        goToMonthIdx(idx);
                      }}
                      className={`w-full text-left px-2 py-1.5 text-[11px] leading-tight transition-colors ${
                        isActive
                          ? "font-bold text-gray-900 bg-gray-100 rounded"
                          : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                      }`}
                    >
                      {MONTHS_RU[month].slice(0, 3)}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Calendar grid */}
            <div className="flex-1 flex flex-col" style={{ minWidth: 0 }}>
              <div
                ref={calendarScrollRef}
                className="flex-1 rmbd-cal px-3 pt-2"
                style={{
                  overflowY: "scroll",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {monthList.map(({ year, month }, idx) => {
                  const mDays = getCalendarDays(year, month);
                  return (
                    <div
                      key={`${year}-${month}`}
                      ref={(el) => (monthRefs.current[idx] = el)}
                      className="mb-5"
                    >
                      <div className="text-xs font-semibold text-gray-900 mb-1.5 text-center">
                        {MONTHS_RU[month]} {year}
                      </div>
                      <div
                        className="grid grid-cols-7 mb-0.5"
                        style={{ fontSize: "14px" }}
                      >
                        {DAYS_RU.map((d, i) => (
                          <div
                            key={d}
                            className={`text-center py-0.5 ${i >= 5 ? "text-red-400 font-semibold" : "text-gray-400"}`}
                          >
                            {d}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7">
                        {mDays.map(({ date, currentMonth }) => {
                          const isStart = checkIn && isSameDay(date, checkIn);
                          const isEnd = checkOut && isSameDay(date, checkOut);

                          // Проверяем, входит ли дата в диапазон
                          const isInRange =
                            checkIn &&
                            effectiveEnd &&
                            effectiveEnd > checkIn &&
                            date > checkIn &&
                            date < effectiveEnd &&
                            !(
                              checkIn &&
                              effectiveEnd &&
                              Array.from({
                                length: Math.ceil(
                                  (effectiveEnd - checkIn) /
                                    (1000 * 60 * 60 * 24),
                                ),
                              }).some((_, i) => {
                                const d = new Date(checkIn);
                                d.setDate(d.getDate() + i + 1);
                                return d < effectiveEnd && isDateBlocked(d);
                              })
                            );

                          const isFirstUnavailable =
                            checkIn &&
                            !checkOut &&
                            date > checkIn &&
                            (!isDateAvailable(date) || isDateBlocked(date)) &&
                            !Array.from({
                              length: Math.ceil(
                                (date - checkIn) / (1000 * 60 * 60 * 24),
                              ),
                            }).some((_, i) => {
                              const d = new Date(checkIn);
                              d.setDate(d.getDate() + i + 1);
                              return (
                                d < date &&
                                (!isDateAvailable(d) || isDateBlocked(d))
                              );
                            });

                          const canSelect =
                            !checkIn ||
                            (!checkOut && date > checkIn && isInRange);

                          return (
                            <DayCell
                              key={`${date.getTime()}`}
                              date={date}
                              currentMonth={currentMonth}
                              isStart={isStart}
                              isEnd={isEnd}
                              isInRange={!!isInRange}
                              isFirstUnavailable={!!isFirstUnavailable}
                              isWeekend={isWeekend(date)}
                              available={isDateAvailable(date)}
                              blocked={isDateBlocked(date)}
                              price={getDatePrice(date)}
                              accentColor={accentColor}
                              textColor={textColor}
                              onDayClick={handleDayClick}
                              onHover={handleHover}
                              onLeave={() => setHovered(null)}
                              canSelect={canSelect}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Book Button + Price Info */}
          <div className="px-5 py-2 border-t border-gray-100 space-y-2">
            {tariffs?.prices?.length > 0 && (
              <div className="flex items-baseline gap-2 justify-center">
                <span className="text-sm text-gray-500 firstUp">
                  {tariffs.prices[0].food_type}
                </span>
                <span className="text-xl font-bold text-gray-900">
                  {tariffs.prices[0].total_cost} ₽
                </span>
                <span className="text-sm text-gray-500">
                  за {dateRangeInfo.nights}{" "}
                  {dateRangeInfo.nights === 1
                    ? "ночь"
                    : dateRangeInfo.nights < 5
                      ? "ночи"
                      : "ночей"}
                </span>
              </div>
            )}
            <Button
              className="w-full"
              style={{ backgroundColor: accentColor, color: textColor }}
              data-food={tariffs.prices[0]?.food_type || ""}
              data-tariff="base"
              onClick={bookingHandler}
              disabled={!checkIn || !checkOut}
            >
              Забронировать
            </Button>
            {tariffs.count > 1 && (
              <button
                onClick={() => setShowTariffs(!showTariffs)}
                className="w-full flex items-center justify-center gap-1 text-black-600 text-sm font-medium transition-colors py-0.5"
                // style={{ color: textColor }}
              >
                Ещё {tariffs.count - 1} {pluralVariants(tariffs.count - 1)} цены
                {showTariffs ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            )}
          </div>

          {/* Tariffs Panel */}
          <div
            className="absolute inset-0 bg-white z-30 transition-transform duration-300 ease-in-out flex flex-col"
            style={{
              transform:
                showTariffs && checkIn && checkOut
                  ? "translateY(0%)"
                  : "translateY(100%)",
            }}
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b bg-gray-50 flex-shrink-0">
              <div className="font-semibold text-gray-900 text-sm">
                Выберите тариф
              </div>
              <button
                onClick={() => setShowTariffs(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {tariffs.prices.length > 0 && (
                <div className="border-t border-gray-100">
                  <div className="px-4 pt-2 pb-0.5">
                    <div className="text-xs text-gray-500 font-medium mb-0.5 text-right">
                      Базовый тариф
                    </div>
                    <div className="text-xs text-red-500 leading-snug mb-1 whitespace-pre-line text-left">
                      <PrintTariffPaymentConditions tariff={tariffs} />
                    </div>
                  </div>
                  {tariffs.prices.map((item, ii) => (
                    <div
                      key={ii}
                      className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-sm text-gray-800">
                        {item.food_type}
                      </span>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-sm font-semibold text-gray-900">
                          {item.total_cost.toLocaleString("ru-RU")} ₽
                        </span>
                        <Button
                          size="sm"
                          data-food={item.food_type || ""}
                          data-tariff="base"
                          onClick={bookingHandler}
                          className="h-7 px-3 text-xs"
                          style={{
                            backgroundColor: accentColor,
                            color: textColor,
                          }}
                        >
                          Забронировать
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {tariffs.variants.length > 0 &&
                tariffs.variants.map((tariff) => (
                  <div
                    key={tariff.id}
                    className="border-t border-red-100 bg-red-50"
                  >
                    <div className="px-4 pt-2 pb-0.5">
                      <div className="text-xs text-gray-500 font-medium mb-0.5 text-right">
                        {tariff.name}
                      </div>
                      <div className="text-xs text-red-500 leading-snug mb-1 whitespace-pre-line text-left">
                        <PrintTariffPaymentConditions tariff={tariff} />
                      </div>
                    </div>
                    {tariff.prices.length > 0 &&
                      tariff.prices.map((item, ii) => (
                        <div
                          key={ii}
                          className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-sm text-gray-800">
                            {item.food_type}
                          </span>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-sm font-semibold text-gray-900">
                              {item.total_cost.toLocaleString("ru-RU")} ₽
                            </span>
                            <Button
                              size="sm"
                              data-food={item.food_type || ""}
                              data-tariff={tariff.id}
                              onClick={bookingHandler}
                              className="h-7 px-3 text-xs"
                              style={{
                                backgroundColor: accentColor,
                                color: textColor,
                              }}
                            >
                              Забронировать
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                ))}
            </div>

            <div className="px-4 py-3 border-t flex-shrink-0">
              <button
                onClick={() => setShowTariffs(false)}
                className="w-full flex items-center justify-center gap-1 text-teal-600 hover:text-teal-700 text-sm font-medium"
              >
                Скрыть
                <ChevronDown className="w-4 h-4 rotate-180" />
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
